// CityPicker — multi-select picker for the user's "where I shop"
// preference. Drives the localized brand catalog (BrandStep filters
// pills by brand.regions[] vs profile.shopping_cities[]) and the
// on-demand regional brand enrichment loop.
//
// Why multi-select:
//   Many users live BETWEEN metros (Ozarks: Springfield + Branson +
//   Joplin) or shop intentionally across cities (work in one,
//   weekend at a cabin in another). A single string forced a false
//   choice. Cap at 3 entries — enough flexibility, doesn't dilute
//   the brand picker into "everything everywhere."
//
// Geolocation is OPTIONAL. The primary path is search-and-pick from
// the curated US_METROS list. The "Use my location" button is a UX
// helper: it requests browser geolocation, snaps to the nearest
// metro, and pre-fills the search field. User still confirms by
// tapping the result. Declining the prompt keeps the picker fully
// usable.

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { US_METROS, searchMetros, findMetro, nearestMetro, reverseGeocode } from "../data/usMetros";
import { useRegionalEnrichmentStatus, progressFromRow } from "../lib/useRegionalEnrichmentStatus";
import { useToast } from "../lib/toast";
import { font, color, radius } from "../lib/mcmTokens";
import { CHIP_TONES } from "../lib/tokens";

/**
 * Props:
 *   value           — string[] of metro keys (e.g. ["us-mo-springfield"])
 *   onChange        — (next: string[]) => void
 *   max             — max selectable cities (default 3)
 *   showGeolocate   — show the "use my location" button (default true)
 *   compact         — true → tighter padding, no header copy
 */
export default function CityPicker({
  value = [],
  onChange,
  max = 3,
  showGeolocate = true,
  compact = false,
}) {
  const [query, setQuery] = useState("");
  const [geolocating, setGeolocating] = useState(false);
  const [geoError, setGeoError] = useState(null);

  // Watch enrichment status for the user's primary city. When it's
  // queued / in_progress, we render an inline status pill IN PLACE
  // OF the "Use my location" button — same row, same slot — so the
  // status reads as "this region is being enriched RIGHT NOW" in
  // context with the city pill, not as a top-pinned banner. Goes
  // back to the geolocate button when status is idle / done.
  const primaryRegion = Array.isArray(value) && value.length > 0 ? value[0] : null;
  const enrichRow = useRegionalEnrichmentStatus(primaryRegion);
  const enrichProgress = progressFromRow(enrichRow);
  const enrichActive = enrichProgress?.status === "in_progress" || enrichProgress?.status === "queued";

  // Toast on completion — fires once when status flips from
  // in_progress/queued → done. The user gets a single notice
  // instead of a constant progress feed (the whole point of
  // dropping the polling).
  const toast = useToast();
  const prevEnrichStatusRef = useRef(enrichProgress?.status || null);
  useEffect(() => {
    const prev = prevEnrichStatusRef.current;
    const curr = enrichProgress?.status || null;
    prevEnrichStatusRef.current = curr;
    if (prev && prev !== "done" && curr === "done") {
      const cityName = (enrichProgress?.displayName || "").split(",")[0] || "your area";
      toast?.push?.(`Local brands ready for ${cityName}`, { tone: "success" });
    }
  }, [enrichProgress?.status, enrichProgress?.displayName, toast]);
  // Candidate from a successful geolocation read — shown in a
  // confirm card so the action reads as "we searched and found
  // X" rather than silently dropping a city in. Carries the
  // user's actual home town (via reverse-geocode) plus the metro
  // it maps to. Confirm adds the metro; cancel discards.
  const [candidate, setCandidate] = useState(null);

  const selected = useMemo(
    () => value.map((k) => findMetro(k)).filter(Boolean),
    [value],
  );

  const canAddMore = selected.length < max;

  const results = useMemo(() => {
    const base = searchMetros(query, 24);
    // Drop already-selected metros from the result list — selecting
    // them again is a no-op. Keep them in `selected` above where the
    // user can deselect.
    const selectedKeys = new Set(value);
    return base.filter((m) => !selectedKeys.has(m.key));
  }, [query, value]);

  const add = useCallback(
    (key) => {
      if (!key) return;
      if (value.includes(key)) return;
      if (value.length >= max) return;
      onChange?.([...value, key]);
      setQuery(""); // clear search after a pick to make space for the next
    },
    [value, max, onChange],
  );

  const remove = useCallback(
    (key) => {
      onChange?.(value.filter((k) => k !== key));
    },
    [value, onChange],
  );

  const useLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoError("Location not available on this device");
      return;
    }
    setGeolocating(true);
    setGeoError(null);
    setCandidate(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const m = nearestMetro(lat, lng);
        if (!m) {
          setGeolocating(false);
          setGeoError("No nearby metros — try searching");
          return;
        }
        // Reverse-geocode in parallel with the metro snap so the
        // candidate card can show the user's ACTUAL town name
        // ("Nixa, MO") alongside the metro we'd add to their
        // shopping list ("Springfield, MO"). If the geocode fails
        // (network blip, Nominatim rate-limit), we fall back to
        // showing just the metro — still functional, just less
        // confidence-inspiring copy.
        const home = await reverseGeocode(lat, lng);
        setGeolocating(false);
        setCandidate({
          home,                 // { town, state, label } | null
          metro: m,             // matched US_METROS row
          alreadySelected: value.includes(m.key),
        });
      },
      (err) => {
        setGeolocating(false);
        // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        if (err.code === 1) setGeoError("Permission denied — pick a city below");
        else setGeoError("Couldn't read location — pick a city below");
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60_000 },
    );
  }, [value]);

  const confirmCandidate = useCallback(() => {
    if (!candidate?.metro) return;
    if (!candidate.alreadySelected) add(candidate.metro.key);
    setCandidate(null);
  }, [candidate, add]);

  const dismissCandidate = useCallback(() => {
    setCandidate(null);
  }, []);

  const padding = compact ? "10px 12px" : "14px 14px";
  const gap     = compact ? 8 : 12;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap }}>
      {!compact && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{
            fontFamily: font.mono,
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: CHIP_TONES.location.fg,
            fontWeight: 600,
          }}>
            Where you shop
          </div>
          <div style={{
            fontFamily: font.detail,
            fontStyle: "italic",
            fontSize: 13,
            color: color.inkMuted,
            lineHeight: 1.35,
          }}>
            Pick up to {max} nearby cities. We use this to surface
            local brands and house-brands from the grocers near you.
          </div>
        </div>
      )}

      {/* Selected pills — tappable to remove */}
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selected.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={(e) => { e.preventDefault(); remove(m.key); }}
              className="mcm-focusable"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px 6px 12px",
                fontFamily: font.detail,
                fontStyle: "italic",
                fontSize: 13,
                fontWeight: 500,
                color: color.cream,
                background: CHIP_TONES.location.fg,
                border: `1px solid ${CHIP_TONES.location.fg}`,
                borderRadius: 999,
                cursor: "pointer",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span>{m.label}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ opacity: 0.7 }}>
                <line x1="3" y1="3" x2="9" y2="9" />
                <line x1="9" y1="3" x2="3" y2="9" />
              </svg>
            </button>
          ))}
        </div>
      )}

      {/* Candidate confirm card — appears after a successful
          geolocation + reverse-geocode. Shows the user's actual
          home town and the metro it'd map to for shopping. The
          two-line layout makes the search feel deliberate rather
          than magic ("we found Nixa, MO and we'd shop the
          Springfield metro for you"). */}
      {candidate && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "12px 14px",
          borderRadius: radius.field || 12,
          background: `${CHIP_TONES.location.fg}10`,
          border: `1px solid ${CHIP_TONES.location.fg}40`,
        }}>
          <div style={{
            fontFamily: font.mono,
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: CHIP_TONES.location.fg,
            fontWeight: 600,
          }}>
            {candidate.alreadySelected ? "Already in your list" : "Found you"}
          </div>
          {/* Explicit "you live in X, we'll shop Y for you" copy.
              Critical for small-town users — without this, someone
              in Nixa, MO sees "Springfield, MO" added to their list
              and thinks the geolocation is bugged ("I DON'T LIVE IN
              SPRINGFIELD"). Stating both the home town and the
              shopping metro explicitly kills that confusion. */}
          <div style={{
            fontFamily: font.detail,
            fontStyle: "italic",
            fontSize: 16,
            color: color.ink,
            lineHeight: 1.4,
          }}>
            {candidate.home?.label ? (
              <>
                You live in <span style={{ fontWeight: 600 }}>{candidate.home.label}</span>,
                {" "}so we'll shop the{" "}
                <span style={{ fontWeight: 600 }}>{candidate.metro.label}</span>
                {" "}area for you.
              </>
            ) : (
              <>
                We'll shop the{" "}
                <span style={{ fontWeight: 600 }}>{candidate.metro.label}</span>
                {" "}area for you.
              </>
            )}
          </div>
          <div style={{
            fontFamily: font.sans,
            fontSize: 11,
            color: color.inkMuted,
            lineHeight: 1.4,
          }}>
            That means brands and store house-brands from grocers
            around {candidate.metro.label.split(",")[0]} surface first
            when you scan or search.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); confirmCandidate(); }}
              disabled={candidate.alreadySelected}
              className="mcm-focusable"
              style={{
                flex: 2,
                padding: "10px 14px",
                fontFamily: font.sans,
                fontSize: 13,
                fontWeight: 600,
                color: candidate.alreadySelected ? color.inkFaint : color.cream,
                background: candidate.alreadySelected
                  ? "transparent"
                  : CHIP_TONES.location.fg,
                border: `1px solid ${candidate.alreadySelected ? color.hairline : CHIP_TONES.location.fg}`,
                borderRadius: radius.field || 10,
                cursor: candidate.alreadySelected ? "default" : "pointer",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {candidate.alreadySelected
                ? "Already added"
                : `Yes, shop the ${candidate.metro.label.split(",")[0]} area`}
            </button>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); dismissCandidate(); }}
              className="mcm-focusable"
              style={{
                flex: 1,
                padding: "10px 14px",
                fontFamily: font.sans,
                fontSize: 13,
                fontWeight: 500,
                color: color.inkMuted,
                background: "transparent",
                border: `1px solid ${color.hairline}`,
                borderRadius: radius.field || 10,
                cursor: "pointer",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search + use-my-location row */}
      {canAddMore && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search cities…"
              style={{
                flex: 1,
                padding,
                fontFamily: font.sans,
                fontSize: 14,
                color: color.ink,
                background: color.cream,
                border: `1px solid ${color.hairline}`,
                borderRadius: radius.field || 12,
                outline: "none",
              }}
            />
            {showGeolocate && enrichActive ? (
              // Inline enrichment status — sits in the SAME slot the
              // geolocate button normally lives in. Hourglass +
              // single-line label, no progress bar (we don't poll for
              // mid-run %, just signal that something's running and
              // we'll notify when it's done).
              <div
                role="status"
                aria-live="polite"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "0 14px",
                  background: "transparent",
                  border: `1px dashed ${CHIP_TONES.location.fg}55`,
                  borderRadius: radius.field || 12,
                  whiteSpace: "nowrap",
                  fontFamily: font.sans,
                  fontSize: 11,
                  fontWeight: 600,
                  color: CHIP_TONES.location.fg,
                  letterSpacing: "0.04em",
                }}
              >
                <InlineHourglass tint={CHIP_TONES.location.fg} />
                <span>
                  {enrichProgress?.status === "queued" ? "Queued…" : "Finding brands…"}
                </span>
              </div>
            ) : showGeolocate ? (
              <button
                type="button"
                onClick={useLocation}
                disabled={geolocating}
                className="mcm-focusable"
                style={{
                  padding: "0 14px",
                  fontFamily: font.sans,
                  fontSize: 12,
                  fontWeight: 500,
                  color: geolocating ? color.inkFaint : color.ink,
                  background: "transparent",
                  border: `1px solid ${color.hairline}`,
                  borderRadius: radius.field || 12,
                  cursor: geolocating ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {geolocating ? "Locating…" : "Use my location"}
              </button>
            ) : null}
          </div>
          {geoError && (
            <div style={{
              fontFamily: font.sans,
              fontSize: 11,
              color: color.inkMuted,
              fontStyle: "italic",
            }}>
              {geoError}
            </div>
          )}
        </div>
      )}

      {/* Result list — only renders when adding more is possible AND
          there's a query to filter against (otherwise the list is the
          full 100+ metros, which is too long to be useful) */}
      {canAddMore && query.trim() && (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          maxHeight: 280,
          overflowY: "auto",
          border: `1px solid ${color.hairline}`,
          borderRadius: radius.field || 12,
          padding: 4,
        }}>
          {results.length === 0 ? (
            <div style={{
              padding: "12px 14px",
              fontFamily: font.sans,
              fontSize: 13,
              color: color.inkMuted,
              textAlign: "center",
            }}>
              No metros match. Try a state name or abbreviation.
            </div>
          ) : (
            results.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={(e) => { e.preventDefault(); add(m.key); }}
                className="mcm-focusable"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  fontFamily: font.detail,
                  fontStyle: "italic",
                  fontSize: 14,
                  fontWeight: 500,
                  color: color.ink,
                  background: "transparent",
                  border: "none",
                  borderRadius: radius.field || 8,
                  cursor: "pointer",
                  textAlign: "left",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                <span>{m.label}</span>
                <span style={{
                  fontFamily: font.mono,
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  color: color.inkFaint,
                  textTransform: "uppercase",
                }}>
                  {m.region}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      {!canAddMore && (
        <div style={{
          fontFamily: font.sans,
          fontSize: 12,
          color: color.inkMuted,
          fontStyle: "italic",
        }}>
          {max} cities selected — remove one to add another.
        </div>
      )}
    </div>
  );
}

// Tiny SVG hourglass — sand grain falling, frame rotates every 6s.
// Used inline in the geolocate slot when an enrichment is running.
// Pure CSS animation; no JS timer cost.
function InlineHourglass({ tint }) {
  return (
    <>
      <style>{`
        @keyframes mcm-citypicker-flip {
          0%, 45%   { transform: rotate(0deg); }
          50%, 95%  { transform: rotate(180deg); }
          100%      { transform: rotate(360deg); }
        }
        @keyframes mcm-citypicker-grain {
          0%   { transform: translateY(-2px); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateY(6px);  opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mcm-citypicker-glass-rot,
          .mcm-citypicker-glass-grain { animation: none; }
        }
      `}</style>
      <svg width="14" height="14" viewBox="0 0 24 24" style={{ flexShrink: 0, color: tint }} aria-hidden>
        <g
          className="mcm-citypicker-glass-rot"
          style={{
            transformOrigin: "12px 12px",
            animation: "mcm-citypicker-flip 6s cubic-bezier(0.65, 0, 0.35, 1) infinite",
          }}
        >
          <path d="M5 3 L19 3 L13 11 L11 11 Z"   fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M5 21 L19 21 L13 13 L11 13 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <line x1="4" y1="3"  x2="20" y2="3"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <line x1="4" y1="21" x2="20" y2="21" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          <line
            className="mcm-citypicker-glass-grain"
            x1="12" y1="11" x2="12" y2="13"
            stroke={tint} strokeWidth="1.4" strokeLinecap="round"
            style={{ animation: "mcm-citypicker-grain 0.9s linear infinite" }}
          />
        </g>
      </svg>
    </>
  );
}
